import React from 'react';
import { Card, Button, Input, Typography, Rate } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { TaskReview } from '../../services/tasks';

const { Text } = Typography;
const { TextArea } = Input;

interface ReviewTabProps {
  isEdit: boolean;
  reviews: TaskReview[];
  reviewText: string;
  reviewRating: number | null;
  setReviewText: React.Dispatch<React.SetStateAction<string>>;
  setReviewRating: React.Dispatch<React.SetStateAction<number | null>>;
  handleAddReview: () => void;
  handleDeleteReview: (reviewId: number | string) => void;
  formatDateTime: (dateString?: string) => string;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({
  isEdit,
  reviews,
  reviewText,
  reviewRating,
  setReviewText,
  setReviewRating,
  handleAddReview,
  handleDeleteReview,
  formatDateTime,
}) => {
  return (
    <Card title="Reviews" bodyStyle={{ padding: '16px' }}>
      {/* Staff Reviews */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Staff Reviews</div>
        <div style={{ marginBottom: 16 }}>
          {reviews.filter((review: TaskReview) => !review.customer_id).map((review, index) => (
            <div
              key={review.id || index}
              style={{
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <Rate disabled value={review.rating || 0} />
                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                  <Text>{review.review || '-'}</Text>
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                By {review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                {isEdit && (
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteReview(review.id!)}
                    style={{ marginLeft: 8, padding: 0 }}
                  />
                )}
              </div>
            </div>
          ))}
          {reviews.filter((review: TaskReview) => !review.customer_id).length === 0 && (
            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
              No staff reviews yet.
            </div>
          )}
        </div>
        {isEdit && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ marginBottom: 8 }}>
              <Rate value={reviewRating || 0} onChange={(value) => setReviewRating(value || null)} />
            </div>
            <TextArea
              rows={6}
              placeholder="Enter review..."
              value={reviewText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewText(e.target.value)}
            />
            <Button
              type="primary"
              onClick={handleAddReview}
              style={{ marginTop: 12, width: '100%' }}
              disabled={!reviewText.trim() || !reviewRating}
            >
              Add Review
            </Button>
          </div>
        )}
      </div>

      {/* Customer Reviews */}
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
        <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Customer Reviews</div>
        {reviews.filter((review: TaskReview) => review.customer_id).length > 0 ? (
          <div>
            {reviews.filter((review: TaskReview) => review.customer_id).map((review: TaskReview) => (
              <div
                key={review.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  <Rate disabled value={review.rating || 0} />
                  <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                    <Text>{review.review || '-'}</Text>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  By {review.customer?.name || review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
            No customer reviews submitted yet
          </div>
        )}
      </div>
    </Card>
  );
};
